import React, { useEffect, useRef, useState } from "react";
import { Upload, Loader2, Trash2, Check } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, resolveUrl } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";

export function useMedia() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return { items, load };
}

export default function MediaLibrary({ selectable = false, onSelect = null, selectedUrl = null }) {
  const { items, load } = useMedia();
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded"); load();
    } catch (e) { toast.error(apiErr(e, "Upload failed")); } finally { setBusy(false); }
  };
  const del = async (id) => { try { await api.delete(`/admin/media/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div data-testid="media-library">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-[#787672]">{items.length} images</p>
        <input ref={inputRef} type="file" accept="image/*" className="hidden" data-testid="media-upload-input"
          onChange={(e) => { const f = e.target.files?.[0]; upload(f); e.target.value = ""; }} />
        <Button data-testid="media-upload-btn" onClick={() => inputRef.current?.click()} disabled={busy} className="rounded-full bg-[#2C3531] hover:bg-[#1e2422]">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> Upload image</>}
        </Button>
      </div>
      {items.length === 0 ? (
        <div className="border border-dashed border-[#E8E6E1] rounded-2xl py-16 text-center text-sm text-[#B0AEA8]">No images yet. Upload your first image.</div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
          {items.map((m) => {
            const active = selectedUrl === m.url;
            return (
              <div key={m.id} data-testid={`media-item-${m.id}`}
                className={`relative group aspect-square rounded-xl overflow-hidden border bg-white ${active ? "border-[#2C3531] ring-2 ring-[#2C3531]" : "border-[#E8E6E1]"}`}>
                <img src={resolveUrl(m.url)} alt={m.filename} className="w-full h-full object-cover" />
                {selectable && (
                  <button onClick={() => onSelect?.(m.url)} data-testid={`media-select-${m.id}`}
                    className="absolute inset-0 flex items-center justify-center bg-[#2C3531]/0 hover:bg-[#2C3531]/40 transition-colors">
                    {active && <span className="w-7 h-7 rounded-full bg-[#2C3531] text-white flex items-center justify-center"><Check className="w-4 h-4" /></span>}
                  </button>
                )}
                {!selectable && (
                  <button onClick={() => del(m.id)} data-testid={`media-delete-${m.id}`}
                    className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-white/90 text-[#993333] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
